import React, { useMemo } from 'react';
import { groupExtraColumnMappingsByApp } from '../../utils/bulkUserCsvAppFields';

/**
 * Shows how extra CSV columns map onto apps assigned to the target group-period.
 */
const BulkCsvColumnMappingPanel = ({
  mappings = [],
  mappingEnabled = {},
  onMappingToggle,
  groupLabel = '',
  periodLabel = '',
  disabled = false
}) => {
  const groups = useMemo(() => groupExtraColumnMappingsByApp(mappings), [mappings]);
  const hasTrackMapping = useMemo(
    () => (mappings || []).some((m) => m.fieldKey === 'track_period_id'),
    [mappings]
  );
  const hasPathwayMapping = useMemo(
    () => (mappings || []).some((m) => m.fieldKey === 'primary_pathway'),
    [mappings]
  );
  const hasVendorMapping = useMemo(
    () => (mappings || []).some((m) => m.fieldKey === 'vendor_groups_periods_id'),
    [mappings]
  );

  if (!groups.length) return null;

  const scopeLabel = [groupLabel, periodLabel].filter(Boolean).join(' · ');

  return (
    <div className="bulk-csv-mapping-panel">
      <p className="profile-sheet-label" id="bulk-csv-mapping-title">
        Column mapping
      </p>
      <p className="profile-sheet-hint">
        {scopeLabel ? (
          <>
            The group-period <strong>{scopeLabel}</strong> has app assignments that match extra columns
            in your file. Each recognized column is mapped below — uncheck any you do not want to import.
          </>
        ) : (
          <>
            This group-period has app assignments that match extra columns in your file. Each recognized
            column is mapped below — uncheck any you do not want to import.
          </>
        )}
      </p>

      {hasTrackMapping ? (
        <p className="bulk-csv-track-hint" role="note">
          Schedule track values may be a short code or full title. The system maps{' '}
          <code>A</code>, <code>B</code>, <code>C</code>, … to periods titled{' '}
          <strong>Track A</strong>, <strong>Track B</strong>, <strong>Track C</strong>, and so on.
          Full titles like <code>Track A</code> also work.
        </p>
      ) : null}

      {hasPathwayMapping ? (
        <p className="bulk-csv-track-hint" role="note">
          Primary pathway values match taxonomy concentration <strong>titles</strong> (not UUIDs). Use
          the same text as in Taxonomy (for example the concentration name). Matching is
          case-insensitive.
        </p>
      ) : null}

      {hasVendorMapping ? (
        <p className="bulk-csv-track-hint" role="note">
          Vendor values match team group names in this organization (for example <code>FA01</code>,{' '}
          <code>FA16</code>). Use the same name as in Groups — the system stores the vendor partner
          group-period for that team.
        </p>
      ) : null}

      {groups.map((group) => (
        <section
          key={group.appSlug}
          className="bulk-csv-mapping-app"
          aria-labelledby={`bulk-csv-mapping-app-${group.appSlug}`}
        >
          <div className="bulk-csv-mapping-app-header">
            <div>
              <h4 className="bulk-csv-mapping-app-title" id={`bulk-csv-mapping-app-${group.appSlug}`}>
                {group.appDisplayName}
              </h4>
              <p className="bulk-csv-mapping-app-meta">Assigned to this group-period</p>
            </div>
            <span className="bulk-csv-mapping-app-badge">App assignment</span>
          </div>

          <ul className="bulk-csv-mapping-list">
            {group.mappings.map((mapping) => {
              const checked = mappingEnabled[mapping.csvHeader] !== false;
              const inputId = `bulk-csv-map-${mapping.csvHeader}`;
              return (
                <li key={mapping.csvHeader} className="bulk-csv-mapping-item">
                  <label className="bulk-csv-mapping-item-label" htmlFor={inputId}>
                    <input
                      id={inputId}
                      type="checkbox"
                      className="bulk-csv-mapping-checkbox"
                      checked={checked}
                      onChange={(e) => onMappingToggle?.(mapping.csvHeader, e.target.checked)}
                      disabled={disabled}
                    />
                    <span className="bulk-csv-mapping-item-main">
                      <span className="bulk-csv-mapping-item-recognized">Recognized</span>
                      <span className="bulk-csv-mapping-item-route">
                        CSV column <code>{mapping.csvHeader}</code>
                        <span className="bulk-csv-mapping-item-arrow" aria-hidden="true">
                          →
                        </span>
                        <span className="bulk-csv-mapping-item-target">{mapping.fieldLabel}</span>
                      </span>
                    </span>
                  </label>
                  <p className="bulk-csv-mapping-item-detail">
                    {checked ? (
                      mapping.fieldKey === 'track_period_id' ? (
                        <>
                          Values from <strong>{mapping.csvHeader}</strong> (e.g. <code>A</code>) will be
                          saved as the matching schedule track period (e.g. <strong>Track A</strong>) on{' '}
                          <strong>{group.appDisplayName}</strong>.
                        </>
                      ) : mapping.fieldKey === 'primary_pathway' ? (
                        <>
                          Values from <strong>{mapping.csvHeader}</strong> match by taxonomy{' '}
                          <strong>title</strong> (concentration name) and are saved as primary pathway on{' '}
                          <strong>{group.appDisplayName}</strong>.
                        </>
                      ) : mapping.fieldKey === 'vendor_groups_periods_id' ? (
                        <>
                          Values from <strong>{mapping.csvHeader}</strong> (e.g. <code>FA01</code>) will
                          be saved as the matching vendor partner group on{' '}
                          <strong>{group.appDisplayName}</strong>.
                        </>
                      ) : (
                        <>
                          Values from <strong>{mapping.csvHeader}</strong> will be saved to the{' '}
                          <strong>{mapping.fieldLabel}</strong> attribute on{' '}
                          <strong>{group.appDisplayName}</strong> for each imported member.
                        </>
                      )
                    ) : (
                      <>
                        Column <strong>{mapping.csvHeader}</strong> will be ignored during import.
                      </>
                    )}
                  </p>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
};

export default BulkCsvColumnMappingPanel;
